import React from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonProps,
  Center,
  Flex,
  HTMLChakraProps,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  useIsFeatureEnabled,
  useIsFreemiumV3Customer,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';
import {
  IntegrationStatus,
  IntegrationType,
} from '../../shared/integrations/common';
import {useNavigate} from 'react-router-dom';
import {AllIntegrations, IntegrationInfo} from './AllIntegrations';
import {
  useEnableIntegration,
  useIntegrations,
} from '../../hooks/api/integrations';
import _ from 'lodash';
import {IntegrationResponse} from '../../shared/api/definitions/integration';

const IntegrationsList = () => {
  const hubspot = useIsFeatureEnabled('hubspot');
  const salesforce = useIsFeatureEnabled('salesforce');
  const {data, isPending, isError} = useIntegrations({});
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();
  const isFreemiumV3Customer = useIsFreemiumV3Customer();
  const isIntentEnabled = useIsFeatureEnabled('intent');

  if (isPending) {
    return (
      <Center>
        <Spinner marginX="auto" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Center>
        <Alert status="error" maxWidth="1048px">
          <AlertIcon />
          There was an error fetching your integrations.
        </Alert>
      </Center>
    );
  }

  const integrations = data.reduce(
    (acc, integration) => {
      acc[integration.type] = integration;
      return acc;
    },
    {} as Partial<Record<IntegrationType, IntegrationResponse>>
  );

  return (
    <Flex direction="column" gap={8}>
      {isFreemiumV3Customer ? (
        <>
          <Flex direction="column" gap={2}>
            <Text fontSize="lg">Intent</Text>
            <Flex gap={8} wrap={'wrap'}>
              <Card
                detailsUrl="linkedin"
                integration={AllIntegrations.linkedin}
                status={integrations.linkedin?.status}
              >
                <OAuthConnectButton
                  integration={integrations.linkedin}
                  oauthUrl={AllIntegrations.linkedin.oauthUrl}
                />
              </Card>
              <Card
                detailsUrl="webIntent"
                integration={AllIntegrations.webIntent}
                status={integrations.webIntent?.status}
              >
                <BasicConnectButton
                  status={integrations.webIntent?.status}
                  type="webIntent"
                />
              </Card>
            </Flex>
          </Flex>
          <Flex direction="column" gap={2}>
            <Text fontSize="lg">CRM</Text>
            <Flex gap={8} wrap={'wrap'}>
              <Card
                detailsUrl="salesforce"
                integration={AllIntegrations.salesforce}
                status={integrations.salesforce?.status}
                isDisabled={true}
              />
              <Card
                detailsUrl="hubspot"
                integration={AllIntegrations.hubspot}
                status={integrations.hubspot?.status}
                isDisabled={true}
              />
            </Flex>
          </Flex>

          <Flex direction="column" gap={2}>
            <Text fontSize="lg">Other</Text>
            <Flex gap={8} wrap={'wrap'}>
              <Card
                detailsUrl="slack"
                integration={AllIntegrations.slack}
                status={integrations.slack?.status}
                isDisabled={true}
              />
            </Flex>
          </Flex>
        </>
      ) : (
        /** Paid customers and legacy freemium customers */
        <>
          <Flex direction="column" gap={2}>
            <Text fontSize="lg">CRM</Text>
            <Flex gap={8} wrap={'wrap'}>
              <Card
                detailsUrl="salesforce"
                integration={AllIntegrations.salesforce}
                status={integrations.salesforce?.status}
                isDisabled={!salesforce || !!integrations.hubspot}
              >
                <OAuthConnectButton
                  integration={integrations.salesforce}
                  oauthUrl={AllIntegrations.salesforce.oauthUrl}
                />
              </Card>
              <Card
                detailsUrl="hubspot"
                integration={AllIntegrations.hubspot}
                status={integrations.hubspot?.status}
                isDisabled={!hubspot || !!integrations.salesforce}
              >
                <OAuthConnectButton
                  integration={integrations.hubspot}
                  oauthUrl={AllIntegrations.hubspot.oauthUrl}
                />
              </Card>
            </Flex>
          </Flex>

          <Flex direction="column" gap={2}>
            <Text fontSize="lg">Intent</Text>
            <Flex gap={8} wrap={'wrap'}>
              <Card
                detailsUrl="linkedin"
                integration={AllIntegrations.linkedin}
                status={integrations.linkedin?.status}
                isDisabled={isFreeOrListBuilderCustomer}
              >
                <OAuthConnectButton
                  integration={integrations.linkedin}
                  oauthUrl={AllIntegrations.linkedin.oauthUrl}
                />
              </Card>
              <Card
                detailsUrl="webIntent"
                integration={AllIntegrations.webIntent}
                isDisabled={!isIntentEnabled}
                status={integrations.webIntent?.status}
              >
                <BasicConnectButton
                  status={integrations.webIntent?.status}
                  type="webIntent"
                />
              </Card>
            </Flex>
          </Flex>

          <Flex direction="column" gap={2}>
            <Text fontSize="lg">Other</Text>
            <Flex gap={8} wrap={'wrap'}>
              <Card
                detailsUrl="slack"
                integration={AllIntegrations.slack}
                status={integrations.slack?.status}
                isDisabled={true}
              />
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

const Card = ({
  detailsUrl,
  integration,
  status,
  isDisabled,
  children,
}: {
  integration: IntegrationInfo;
  detailsUrl: string;
  status?: IntegrationStatus;
  isDisabled?: boolean;
  children?: React.ReactNode;
}) => {
  const navigate = useNavigate();

  return (
    <Flex
      direction="column"
      position="relative"
      borderColor={'kgray.200'}
      borderWidth="1px"
      borderRadius="8px"
      p={6}
      w={'320px'}
      {...(status
        ? {
            onClick: () => navigate(detailsUrl),
            cursor: 'pointer',
            _hover: {bgColor: 'kgray.100'},
          }
        : {})}
    >
      {!isDisabled && children && (
        <Box position="absolute" top={4} right={4}>
          {children}
        </Box>
      )}
      <Flex width="75px" height="75px" marginBottom={4}>
        {integration.logo}
      </Flex>
      <Box marginBottom={2} fontSize="16px" lineHeight="1" fontWeight={500}>
        {integration.label}
      </Box>
      <Box fontSize="14px" color="kgray.300">
        {integration.description}
      </Box>
    </Flex>
  );
};

const OAuthConnectButton = ({
  integration,
  oauthUrl,
}: {
  integration?: IntegrationResponse;
  oauthUrl: string;
}) => {
  let buttonText, buttonProps: ButtonProps & HTMLChakraProps<'a'>;
  if (integration?.status === 'active') {
    buttonText = 'Connected';
    buttonProps = {
      colorScheme: 'kbuttongreen',
      color: 'kgreen.400',
      isDisabled: true,
      // hack: use inline style to override disabled button css
      // this case isn't really a button
      style: {opacity: 1, cursor: 'default'},
    };
  } else if (integration?.status === 'refresh_token_expired') {
    // refresh_token_expired
    buttonText = 'Reactivate';
    buttonProps = {
      colorScheme: 'kbuttonblue',
      onClick: (e) => {
        e.stopPropagation();
        location.assign(oauthUrl);
      },
    };
  } else {
    buttonText = 'Connect';
    buttonProps = {
      colorScheme: 'kbuttonblue',
      cursor: 'pointer',
      onClick: (e) => {
        e.stopPropagation();
        location.assign(oauthUrl);
      },
    };
  }

  return <Button {...buttonProps}>{buttonText}</Button>;
};

const BasicConnectButton = ({
  status,
  type,
}: {
  status?: IntegrationStatus;
  type: 'webIntent';
}) => {
  const enableIntegration = useEnableIntegration();
  const navigate = useNavigate();

  let buttonText, buttonProps: ButtonProps & HTMLChakraProps<'a'>;
  if (status === 'active') {
    buttonText = 'Connected';
    buttonProps = {
      colorScheme: 'kbuttongreen',
      color: 'kgreen.400',
      isDisabled: true,
      style: {opacity: 1, cursor: 'default'},
    };
  } else {
    buttonText = 'Connect';
    buttonProps = {
      colorScheme: 'kbuttonblue',
      cursor: 'pointer',
      onClick: (e) => {
        e.stopPropagation();
        enableIntegration.mutate(
          {type},
          {
            onSuccess: () => {
              navigate(`/settings/integrations/${type}`);
            },
          }
        );
      },
    };
  }

  return (
    <Button {...buttonProps} isLoading={enableIntegration.isPending}>
      {buttonText}
    </Button>
  );
};

export default IntegrationsList;
